import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
//@ts-ignore
import logo from "../../assets/logo-white.png";
//@ts-ignore
import { Container } from "reactstrap";
import { CasinoAPI } from "../../api/casino";
import { SportsBettingAPI } from "../../api/sportsBetting";
import { getCommaSeparatedString } from "../../constants/Helper";
import HtmlContent from "../../components/HTMLContent";
import {
  CasinoResponse,
  SportsBettingResponse,
} from "../../constants/responses";
import PreLoader from "../../components/PreLoader";

interface TableProps {
  title: string;
  value: string;
  link?: string;
}

const TableRow = (props: TableProps) => {
  const { title, value, link = "" } = props;
  return (
    <div
      className="grid grid-cols-6 border-gray-500"
      style={{
        borderLeftWidth: 0.5,
        borderRightWidth: 0.5,
        borderBottomWidth: 0.5,
      }}
    >
      <div className="h-full col-span-2 lg:text-[1.3rem] flex justify-start items-start font-semibold px-8 py-4 border-r border-gray-500">
        {title}
      </div>
      <div className="h-full col-span-4 lg:text-[1.3rem] flex justify-start items-start font-semibold px-8 py-4">
        {link.length > 0 ? (
          <a target="_blank" rel="noopener noreferrer" href={link}>
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

interface LocationState {
  allowed: boolean;
  type: string;
  flag: string;
  id: string;
}

const DetailsPage = () => {
  const location = useLocation();
  const state = location.state as LocationState;
  const [data, setData] = useState<CasinoResponse | SportsBettingResponse>();

  useEffect(() => {
    const fetchCasino = async (id: string) => {
      try {
        const res = await CasinoAPI.getCasinoDetails(id);
        if (res.data) setData(res.data);
      } catch {
        console.log("Something went wrong. Try Again!");
      }
    };

    const fetchSport = async (id: string) => {
      try {
        const res = await SportsBettingAPI.getSportDetails(id);
        if (res.data) setData(res.data);
      } catch {
        console.log("Something went wrong. Try Again!");
      }
    };

    if (state.type === "casino") fetchCasino(state.id);
    else if (state.type === "sport") fetchSport(state.id);
  }, [state.type, state.id]);

  if (!data) return <PreLoader />;
  return (
    <Container
      fluid
      className="m-0 flex flex-col min-h-full w-full items-center text-white"
    >
      {/* Navbar */}
      <section className="w-full lg:w-[75%] lg:mt-[2rem] mb-2">
        <Link to="/">
          <img
            src={logo}
            alt="Casino24"
            className="object-cover w-[10rem] lg:w-[15rem] my-3 cursor-pointer"
          />
        </Link>
      </section>
      {/* Casino Logo & Stats */}
      <section className="w-[75%] md:w-full lg:w-[75%] bg-[#444754] rounded-2xl min-h-[17vh] grid md:grid-cols-2">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center">
          {/* Casino Logo */}
          <img
            alt="Casino Logo"
            src={data.picture}
            className="lg:h-[13rem] w-full object-fit rounded-xl"
          />
          {/* Ratings and Stuff */}
          <div className="h-full col-span-2 p-4 flex flex-col justify-center items-center md:items-start">
            {/* Bonus Description */}
            <h2 className="text-[1rem] lg:text-[1.5rem] font-bold text-center">
              {data.welcomeBonusText} {data.freeBonusText}
            </h2>
            {/* Rating Stars */}
            <div className="flex justify-center lg:justify-start items-start space-x-1 mt-[1rem]">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className={`w-[1.5rem] h-[1.5rem] ${
                    i < 5 ? "text-yellow-400" : "text-gray-400"
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.964a1 1 0 00.95.69h4.188c.969 0 1.371 1.24.588 1.81l-3.39 2.464a1 1 0 00-.364 1.118l1.286 3.964c.3.921-.755 1.688-1.538 1.118L10 14.347l-3.39 2.464c-.783.57-1.838-.197-1.538-1.118l1.286-3.964a1 1 0 00-.364-1.118L2.606 9.391c-.783-.57-.381-1.81.588-1.81h4.188a1 1 0 00.95-.69l1.286-3.964z" />
                </svg>
              ))}
            </div>
            {/* Location & Permission Status */}
            <h2 className="flex justify-center items-center font-bold text-[0.8rem] mt-[1rem]">
              <img
                alt="Flag"
                src={state.flag}
                className="mr-3 w-[2rem] h-[1rem]"
              />
              {!state.allowed ? "You Cannot Play " : "You Can Play"}
            </h2>
          </div>
        </div>
        {/* Button Div */}
        <div className="flex items-center justify-center md:justify-end px-3 text-center">
          <button
            disabled={!state.allowed}
            onClick={() => window.open(data.refURL, "_blank")}
            className={`text-[1rem] lg:text-[1.3rem] py-[1rem] w-[85%] md:w-[40%] rounded-3xl text-white
          font-bold mb-2 select-none border ${
            !state.allowed ? "cursor-not-allowed" : "cursor-pointer"
          }`}
          >
            Play Now
          </button>
        </div>
      </section>
      {/* Details Table */}
      <section className="w-full lg:w-[75%] min-h-[30vh] my-[3rem]">
        <div
          className="grid grid-cols-6 bg-[#444754] h-[6rem] lg:h-[8rem]"
          style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
        >
          <div className="h-full col-span-2 text-[1.2rem] lg:text-[2.5rem] flex justify-center items-center font-bold">
            Overview
          </div>
          <div className="h-full col-span-4 text-[1.2rem] lg:text-[2.5rem] p-2 flex justify-center items-center font-bold">
            Details
          </div>
        </div>

        <TableRow
          title="Website:"
          value={data.website ?? ""}
          link={data.website ?? ""}
        />
        <TableRow title="E-Mail:" value={data.email ?? ""} />
        <TableRow title="Product Offers:" value={data.category?.value ?? ""} />
        <TableRow
          title="Customer Support:"
          value={
            data.customerSupport
              ? getCommaSeparatedString(data.customerSupport)
              : ""
          }
        />
        <TableRow
          title="Languages Covered:"
          value={data.languages ? getCommaSeparatedString(data.languages) : ""}
        />
        <TableRow
          title="Mobile Application:"
          value={data.mobileApp?.value ?? "No"}
        />
        <TableRow
          title="Allowed Countries:"
          value={
            data.allowedCountries
              ? getCommaSeparatedString(data.allowedCountries)
              : "None"
          }
        />
      </section>
      {/*Casino Details */}
      <section className="w-full lg:w-[75%] mb-[2.5rem]">
        {data.description && (
          <>
            <h2 className="text-[2.3rem] font-bold mb-[2rem]">Details</h2>
            <HtmlContent htmlString={data.description} />
          </>
        )}

        {data.bonusLongDesc && (
          <>
            <h2 className="text-[2.3rem] font-bold mb-[2rem] mt-[3rem]">
              Bonus Details
            </h2>
            <HtmlContent htmlString={data.bonusLongDesc} />
          </>
        )}
      </section>
      {/* <h2 className="text-[2.3rem] font-bold mb-[2rem] mt-[3rem]">
          Favourite Games
        </h2> */}
      {/* <GameCarousel games={FavGames} /> */}{" "}
      {/* <h2 className="text-[2.3rem] font-bold mb-[2rem] mt-[3rem]">
          Favourite Games
        </h2> */}
      {/* <GameCarousel games={FavGames} /> */}
    </Container>
  );
};

export default DetailsPage;
